import GlobalHeadline from "../../../GlobalHeadline";
import Reveal from "../../Reveal";
import SectionHeading from "../../SectionHeading";
import SpotlightCard from "../../SpotlightCard";
import { howItWorksSections } from "../../../../content/howItWorksSections";

const STROKE = "rgba(255,255,255,0.4)";

/** Bar chart with a flagged, flatlined bar — a report showing $0. */
const NumbersGraphic = () => (
  <svg viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="10" y1="74" x2="110" y2="74" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
    <rect x="20" y="50" width="14" height="24" fill="rgba(255,255,255,0.06)" stroke={STROKE} strokeWidth="1.2" />
    <rect x="46" y="26" width="14" height="48" fill="rgba(255,255,255,0.06)" stroke={STROKE} strokeWidth="1.2" />
    <rect x="72" y="72" width="14" height="2" fill="rgba(255,255,255,0.06)" stroke={STROKE} strokeWidth="1.2" />
    <line x1="79" y1="50" x2="79" y2="66" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeDasharray="2 3" />
    <text x="79" y="38" textAnchor="middle" fontSize="16" fontFamily="Public Sans, sans-serif" fontWeight="400" fill="rgba(255,255,255,0.85)">
      $0
    </text>
  </svg>
);

/** Monitor with a jagged crack and a connector that trails off into nothing. */
const SystemBreakGraphic = () => (
  <svg viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="15" y="16" width="90" height="44" rx="3" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" />
    <line x1="52" y1="60" x2="52" y2="70" stroke={STROKE} strokeWidth="1.2" />
    <line x1="68" y1="60" x2="68" y2="70" stroke={STROKE} strokeWidth="1.2" />
    <line x1="40" y1="70" x2="80" y2="70" stroke={STROKE} strokeWidth="1.2" />
    <polyline
      points="24,36 42,36 34,23 58,48 50,36 70,36"
      fill="none"
      stroke="rgba(255,255,255,0.6)"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line x1="80" y1="36" x2="100" y2="36" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2" strokeDasharray="3 4" />
  </svg>
);

/** Shield outline with a deliberate, marked gap in its edge. */
const SecurityGapGraphic = () => (
  <svg viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M60 16 L86 28 V46" stroke={STROKE} strokeWidth="1.2" strokeLinecap="round" />
    <path
      d="M86 52 C86 64 76 72 60 78 C44 72 34 64 34 52 V28 L60 16"
      stroke={STROKE}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="86" cy="46" r="2" fill="rgba(255,255,255,0.7)" />
    <circle cx="86" cy="52" r="2" fill="rgba(255,255,255,0.7)" />
  </svg>
);

/** Mismatched, unconnected shapes — scattered vendor tools with no shared system. */
const PatchworkGraphic = () => (
  <svg viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="15" width="28" height="24" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" />
    <rect x="52" y="10" width="20" height="18" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" strokeDasharray="3 3" transform="rotate(6 62 19)" />
    <circle cx="94" cy="24" r="13" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" />
    <rect x="22" y="55" width="20" height="20" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" strokeDasharray="2 4" transform="rotate(-8 32 65)" />
    <rect x="64" y="52" width="32" height="22" fill="rgba(255,255,255,0.05)" stroke={STROKE} strokeWidth="1.2" />
  </svg>
);

const graphics = {
  numbers: NumbersGraphic,
  systemBreak: SystemBreakGraphic,
  securityGap: SecurityGapGraphic,
  patchwork: PatchworkGraphic,
};

/**
 * Problem cards outlining the hidden operational costs this page addresses,
 * each led by a small custom graphic illustrating its specific scenario.
 * Translation: Problem cards outlining the hidden operational costs this page addresses,
 * each led by a small custom graphic illustrating its specific scenario.
 *
 * @returns {JSX.Element} Problems section.
 */
const Problems = () => {
  const { problems } = howItWorksSections;

  return (
    <section id="problem" className="section-block">
      <div className="section-shell">
        <SectionHeading eyebrow={problems.eyebrow} title={problems.title} description={problems.description} />

        <div className="card-grid card-grid--four card-grid--flush">
          {problems.cards.map((card, index) => {
            const Graphic = graphics[card.icon];

            return (
              <Reveal key={card.title} delay={index * 100}>
                <SpotlightCard className="glass-panel section-card group">
                  <div className="hiw-problem-graphic" aria-hidden="true">
                    <Graphic />
                  </div>
                  <GlobalHeadline as="h3">{card.title}</GlobalHeadline>
                  <p>{card.description}</p>
                  {card.detail && <p className="hiw-detail-text">{card.detail}</p>}
                </SpotlightCard>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Problems;
